import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { logger, ValidationError } from "@/lib/logger";
import { QueryError } from "@/components/shared/QueryError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface Company { legal_name?: string; address_line1?: string; address_line2?: string; city?: string; state?: string; postal_code?: string; country?: string; ein?: string | null; email?: string; phone?: string; website?: string }
interface Invoicing { reverse_charge_mention?: string; footer?: string }
interface Defaults { currency?: string; deposit_pct?: number; payment_days?: number; quote_validity_days?: number }

function useSettings() {
  return useQuery({
    queryKey: ["settings", "all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("settings").select("key, value").in("key", ["company", "invoicing", "defaults"]);
      if (error) throw error;
      const by = Object.fromEntries((data ?? []).map((r) => [r.key, r.value]));
      return { company: (by.company ?? {}) as Company, invoicing: (by.invoicing ?? {}) as Invoicing, defaults: (by.defaults ?? {}) as Defaults };
    },
  });
}

function Field({ id, label, value, onChange, placeholder, type = "text", hint }: { id: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string; hint?: string }) {
  return (
    <div className="min-w-0 space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function CompanyTab() {
  const { data, error, isLoading } = useSettings();
  if (error) return <QueryError error={error} />;
  if (isLoading || !data) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  return <CompanyForm key={JSON.stringify(data)} initial={data} />;
}

function CompanyForm({ initial }: { initial: { company: Company; invoicing: Invoicing; defaults: Defaults } }) {
  const qc = useQueryClient();
  const [c, setC] = useState<Company>(initial.company);
  const [inv, setInv] = useState<Invoicing>(initial.invoicing);
  const [d, setD] = useState<Defaults>(initial.defaults);
  const [err, setErr] = useState<string | null>(null);
  const set = <K extends keyof Company>(k: K) => (v: string) => setC((p) => ({ ...p, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      if (!c.legal_name?.trim()) throw new ValidationError("La raison sociale est requise : elle figure sur tous les documents.");
      const num = (v: unknown, label: string, max: number) => { const n = Number(v); if (Number.isNaN(n) || n < 0 || n > max) throw new ValidationError(`${label} : valeur invalide.`); return n; };
      const payload = [
        { key: "company", value: { ...c, legal_name: c.legal_name.trim(), ein: c.ein?.trim() || null } },
        { key: "invoicing", value: inv },
        { key: "defaults", value: { ...d, deposit_pct: num(d.deposit_pct ?? 0, "Acompte", 100), payment_days: num(d.payment_days ?? 30, "Délai de paiement", 365), quote_validity_days: num(d.quote_validity_days ?? 30, "Validité des devis", 365) } },
      ];
      for (const row of payload) {
        const { error } = await supabase.from("settings").update({ value: row.value as never, updated_by: (await supabase.auth.getUser()).data.user?.id }).eq("key", row.key);
        if (error) throw error;
      }
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["settings"] }); setErr(null); toast.success("Paramètres société enregistrés"); },
    onError: (e) => { setErr(e instanceof Error ? e.message : "Enregistrement impossible."); if (!(e instanceof ValidationError)) logger.error("settings.company", e); },
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4" data-testid="company-form">
      <section className="glass rounded-2xl p-5">
        <h3 className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Identité légale</h3>
        <p className="mb-4 text-sm text-muted-foreground">Ces informations sont imprimées sur les devis, factures et contrats. Elles sont <strong>figées dans chaque document au moment de son émission</strong> : les modifier ici ne réécrit jamais un document déjà émis.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="co-name" label="Raison sociale *" value={c.legal_name ?? ""} onChange={set("legal_name")} placeholder="IRONCLIC, Inc." />
          <Field id="co-ein" label="EIN" value={c.ein ?? ""} onChange={set("ein")} placeholder="00-0000000" hint="Numéro d'identification fiscale américain." />
          <Field id="co-a1" label="Adresse" value={c.address_line1 ?? ""} onChange={set("address_line1")} />
          <Field id="co-a2" label="Complément d'adresse" value={c.address_line2 ?? ""} onChange={set("address_line2")} />
          <Field id="co-city" label="Ville" value={c.city ?? ""} onChange={set("city")} />
          <Field id="co-state" label="État / région" value={c.state ?? ""} onChange={set("state")} />
          <Field id="co-zip" label="Code postal" value={c.postal_code ?? ""} onChange={set("postal_code")} />
          <Field id="co-country" label="Pays" value={c.country ?? ""} onChange={set("country")} placeholder="US" />
          <Field id="co-email" label="Email de facturation" value={c.email ?? ""} onChange={set("email")} type="email" />
          <Field id="co-phone" label="Téléphone" value={c.phone ?? ""} onChange={set("phone")} />
          <Field id="co-web" label="Site web" value={c.website ?? ""} onChange={set("website")} />
        </div>
      </section>

      <section className="glass rounded-2xl p-5">
        <h3 className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Mentions de facturation</h3>
        <p className="mb-4 text-sm text-muted-foreground">La mention d'autoliquidation s'imprime sur les factures des clients européens hors États-Unis ; le pied de page apparaît en bas de chaque facture.</p>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="co-rc">Mention d'autoliquidation</Label><Input id="co-rc" value={inv.reverse_charge_mention ?? ""} onChange={(e) => setInv({ ...inv, reverse_charge_mention: e.target.value })} /></div>
          <div className="space-y-1.5"><Label htmlFor="co-footer">Pied de page des factures</Label><Textarea id="co-footer" rows={3} value={inv.footer ?? ""} onChange={(e) => setInv({ ...inv, footer: e.target.value })} placeholder="Coordonnées bancaires, mentions de retard…" /></div>
        </div>
      </section>

      <section className="glass rounded-2xl p-5">
        <h3 className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Conditions par défaut</h3>
        <p className="mb-4 text-sm text-muted-foreground">Valeurs proposées pour un nouveau document. Chaque client, puis chaque document, peut les remplacer.</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field id="co-cur" label="Devise" value={d.currency ?? "EUR"} onChange={(v) => setD({ ...d, currency: v.toUpperCase() })} placeholder="EUR" />
          <Field id="co-dep" label="Acompte (%)" type="number" value={String(d.deposit_pct ?? 30)} onChange={(v) => setD({ ...d, deposit_pct: Number(v) })} />
          <Field id="co-pay" label="Délai de paiement (jours)" type="number" value={String(d.payment_days ?? 30)} onChange={(v) => setD({ ...d, payment_days: Number(v) })} />
          <Field id="co-val" label="Validité des devis (jours)" type="number" value={String(d.quote_validity_days ?? 30)} onChange={(v) => setD({ ...d, quote_validity_days: Number(v) })} />
        </div>
      </section>

      {err && <p role="alert" className="text-sm text-destructive">{err}</p>}
      <div className="flex justify-end"><Button type="submit" disabled={save.isPending}>{save.isPending ? "Enregistrement…" : "Enregistrer"}</Button></div>
    </form>
  );
}
